import { useEffect, useRef, useState } from "react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import api, { API } from "../lib/api";
import { maskPhoneBR } from "../lib/format";
import { formatDatePt, isPast } from "../lib/format";
import RaceCard from "../components/RaceCard";
import { toast } from "sonner";
import {
  Shield,
  LogOut,
  Settings,
  Copy,
  Camera,
  ArrowLeft,
  X,
  KeyRound,
  Lock,
  Flag,
  MapPin,
  Calendar,
  Upload,
  Loader2,
} from "lucide-react";

const SESSION_KEY = "sf_passport_session";

function loadSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveSession(session) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

function clearSession() {
  localStorage.removeItem(SESSION_KEY);
}

export default function PassportPage() {
  const [session, setSession] = useState(() => loadSession());
  const [step, setStep] = useState("phone"); // 'phone' | 'pin' | 'set-pin' | 'logged'
  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [newPinConfirm, setNewPinConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [instaMode, setInstaMode] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    if (session?.pin && session?.phone) {
      setStep("logged");
    }
  }, [session]);

  // Refresh profile from backend when logged
  useEffect(() => {
    if (step !== "logged" || !session?.phone || !session?.pin) return;
    api
      .post("/passport/login", { phone: session.phone, pin: session.pin })
      .then((res) => {
        const next = { ...session, ...res.data, phone: session.phone, pin: session.pin };
        setSession(next);
        saveSession(next);
      })
      .catch(() => {
        // pin invalid → force re-login
        clearSession();
        setSession(null);
        setStep("phone");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const submitPhone = async (e) => {
    e.preventDefault();
    const digits = phone.replace(/\D/g, "");
    if (digits.length !== 11) {
      toast.error("Digite o WhatsApp completo com DDD");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/passport/access", { phone });
      if (res.data.first_access) {
        setStep("set-pin");
      } else {
        setStep("pin");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível liberar o passaporte");
    } finally {
      setLoading(false);
    }
  };

  const submitSetPin = async (e) => {
    e.preventDefault();
    const p = (newPin || "").trim();
    const c = (newPinConfirm || "").trim();
    if (p.length !== 4 || !/^\d{4}$/.test(p)) {
      toast.error("Escolha um PIN de 4 dígitos numéricos");
      return;
    }
    if (p !== c) {
      toast.error("Os PINs não conferem");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/passport/set-pin", {
        phone,
        pin: p,
        pin_confirm: c,
      });
      const next = { ...res.data, phone, pin: p };
      saveSession(next);
      setSession(next);
      setNewPin("");
      setNewPinConfirm("");
      toast.success("PIN definido! Bem-vindo ao Passaporte Elite.");
      setStep("logged");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao salvar PIN");
    } finally {
      setLoading(false);
    }
  };

  const submitPin = async (e) => {
    e.preventDefault();
    if (pin.trim().length !== 4) {
      toast.error("PIN de 4 dígitos");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/passport/login", { phone, pin: pin.trim() });
      const next = { ...res.data, phone, pin: pin.trim() };
      saveSession(next);
      setSession(next);
      setStep("logged");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "PIN incorreto");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    clearSession();
    setSession(null);
    setPin("");
    setPhone("");
    setNewPin("");
    setNewPinConfirm("");
    setStep("phone");
  };

  if (step === "logged" && session) {
    return (
      <PassportProfileView
        session={session}
        onLogout={logout}
        instaMode={instaMode}
        setInstaMode={setInstaMode}
        editOpen={editOpen}
        setEditOpen={setEditOpen}
        onProfileSaved={(updated) => {
          const next = { ...session, ...updated };
          saveSession(next);
          setSession(next);
        }}
      />
    );
  }

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-10 sm:py-16">
      <div className="mb-6">
        <div className="label-caps" style={{ color: "#00E5FF" }}>
          Área do Piloto
        </div>
        <h1 className="font-display text-4xl sm:text-5xl text-white uppercase flex items-center gap-3 mt-1">
          <Shield size={32} style={{ color: "#39FF14" }} />
          Passaporte Elite
        </h1>
        <p className="text-white/60 text-sm mt-2">
          Acesso liberado para pilotos com 3+ rolês oficiais Speed Freaks.
        </p>
      </div>

      {step === "phone" && (
        <form
          onSubmit={submitPhone}
          className="border border-white/10 bg-[#12141D] p-6 space-y-5"
          data-testid="passport-phone-form"
        >
          <div>
            <Label className="label-caps block mb-2">WhatsApp *</Label>
            <Input
              type="tel"
              inputMode="numeric"
              value={phone}
              onChange={(e) => setPhone(maskPhoneBR(e.target.value))}
              placeholder="(85) 91234-5678"
              maxLength={16}
              className="bg-[#0B0D14] border-white/10 focus:border-[#00E5FF] rounded-sm h-11"
              data-testid="passport-phone-input"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="btn-primary w-full h-12"
            data-testid="passport-continue-btn"
          >
            {loading ? "Validando..." : "Liberar Passaporte"}
          </Button>
        </form>
      )}

      {step === "pin" && (
        <form
          onSubmit={submitPin}
          className="border border-white/10 bg-[#12141D] p-6 space-y-5"
          data-testid="passport-pin-form"
        >
          <button
            type="button"
            onClick={() => {
              setStep("phone");
              setPin("");
            }}
            className="inline-flex items-center gap-2 text-xs text-white/60 hover:text-white"
          >
            <ArrowLeft size={12} /> Trocar telefone
          </button>
          <div className="text-white/70 text-sm">
            Piloto identificado. Insira seu <b>PIN de 4 dígitos</b>.
          </div>
          <div>
            <Label className="label-caps block mb-2 flex items-center gap-2">
              <KeyRound size={12} /> PIN
            </Label>
            <Input
              type="text"
              inputMode="numeric"
              autoFocus
              value={pin}
              onChange={(e) =>
                setPin(e.target.value.replace(/\D/g, "").slice(0, 4))
              }
              placeholder="****"
              maxLength={4}
              className="bg-[#0B0D14] border-white/10 focus:border-[#00E5FF] rounded-sm h-14 text-center font-display text-3xl tracking-[0.5em]"
              data-testid="passport-pin-input"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="btn-primary w-full h-12"
            data-testid="passport-login-btn"
          >
            {loading ? "Entrando..." : "Entrar"}
          </Button>
        </form>
      )}

      {step === "set-pin" && (
        <form
          onSubmit={submitSetPin}
          className="border border-white/10 bg-[#12141D] p-6 space-y-5"
          data-testid="passport-set-pin-form"
        >
          <button
            type="button"
            onClick={() => {
              setStep("phone");
              setNewPin("");
              setNewPinConfirm("");
            }}
            className="inline-flex items-center gap-2 text-xs text-white/60 hover:text-white"
          >
            <ArrowLeft size={12} /> Trocar telefone
          </button>
          <div>
            <div className="label-caps" style={{ color: "#39FF14", letterSpacing: "0.3em" }}>
              Primeiro Acesso — Crie seu PIN
            </div>
            <p className="text-white/70 text-sm mt-2 leading-snug">
              Escolha um <b>PIN de 4 dígitos</b> só seu — é a chave do seu Passaporte Elite. Anote com cuidado: <b>não temos como recuperá-lo</b> depois.
            </p>
          </div>
          <div>
            <Label className="label-caps block mb-2 flex items-center gap-2">
              <KeyRound size={12} /> Novo PIN
            </Label>
            <Input
              type="password"
              inputMode="numeric"
              autoFocus
              value={newPin}
              onChange={(e) =>
                setNewPin(e.target.value.replace(/\D/g, "").slice(0, 4))
              }
              placeholder="****"
              maxLength={4}
              className="bg-[#0B0D14] border-white/10 focus:border-[#39FF14] rounded-sm h-14 text-center font-display text-3xl tracking-[0.5em]"
              data-testid="passport-new-pin-input"
            />
          </div>
          <div>
            <Label className="label-caps block mb-2">Confirmar PIN</Label>
            <Input
              type="password"
              inputMode="numeric"
              value={newPinConfirm}
              onChange={(e) =>
                setNewPinConfirm(e.target.value.replace(/\D/g, "").slice(0, 4))
              }
              placeholder="****"
              maxLength={4}
              className="bg-[#0B0D14] border-white/10 focus:border-[#39FF14] rounded-sm h-14 text-center font-display text-3xl tracking-[0.5em]"
              data-testid="passport-new-pin-confirm-input"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="btn-primary w-full h-12"
            data-testid="passport-set-pin-btn"
          >
            {loading ? "Salvando..." : "Definir PIN e Entrar"}
          </Button>
        </form>
      )}
    </div>
  );
}

function PassportProfileView({
  session,
  onLogout,
  instaMode,
  setInstaMode,
  editOpen,
  setEditOpen,
  onProfileSaved,
}) {
  const [changePinOpen, setChangePinOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const rider = {
    name: session.rider_name || "Piloto Speed Freaks",
    motorcycle_model: session.motorcycle_model,
    photo_url: session.photo_url,
    rides_count: session.rides_count || 0,
  };

  if (instaMode) {
    return (
      <InstaSnapshotOverlay
        rider={rider}
        onClose={() => setInstaMode(false)}
      />
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <div>
          <div className="label-caps" style={{ color: "#00E5FF" }}>
            Passaporte Elite
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-white uppercase">
            Olá, {rider.name?.split(" ")[0] || "Piloto"}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setEditOpen(true)}
            className="border border-white/15 hover:border-[#00E5FF] p-2.5 text-white transition-colors"
            title="Editar meus dados"
            data-testid="passport-edit-btn"
          >
            <Settings size={16} />
          </button>
          <button
            onClick={() => setChangePinOpen(true)}
            className="border border-white/15 hover:border-[#F59E0B] hover:text-[#F59E0B] p-2.5 text-white transition-colors"
            title="Trocar meu PIN"
            data-testid="passport-change-pin-btn"
          >
            <Lock size={16} />
          </button>
          <button
            onClick={onLogout}
            className="border border-white/15 hover:border-white p-2.5 text-white transition-colors inline-flex items-center gap-2 text-xs uppercase tracking-widest"
            data-testid="passport-logout-btn"
          >
            <LogOut size={14} /> <span className="hidden sm:inline">Sair</span>
          </button>
        </div>
      </div>

      <RaceCard rider={rider} variant="profile" accent="#00E5FF" testId="passport-race-card" />

      <div className="mt-5 flex flex-wrap gap-3">
        <button
          onClick={() => setInstaMode(true)}
          className="btn-primary inline-flex items-center gap-2 px-5 py-3 text-xs"
          data-testid="passport-insta-btn"
        >
          <Camera size={14} /> 📸 Preparar para Instagram
        </button>
        {/* <button
          onClick={() => setEditOpen(true)}
          className="border border-white/15 hover:border-[#39FF14] px-5 py-3 text-xs uppercase tracking-widest text-white inline-flex items-center gap-2 transition-colors"
          data-testid="passport-edit-inline-btn"
        >
          <Settings size={14} /> Editar meus dados
        </button> */}
        <button
          onClick={() => setHistoryOpen(true)}
          className="border border-white/15 hover:border-[#00E5FF] hover:text-[#00E5FF] px-5 py-3 text-xs uppercase tracking-widest text-white inline-flex items-center gap-2 transition-colors"
          data-testid="passport-history-btn"
        >
          🏁 Meus Rastros
        </button>
      </div>

      {editOpen && (
        <EditProfileDialog
          session={session}
          onClose={() => setEditOpen(false)}
          onSaved={(updated) => {
            const { _keepOpen, ...clean } = updated;
            onProfileSaved(clean);
            if (!_keepOpen) setEditOpen(false);
          }}
        />
      )}

      {changePinOpen && (
        <ChangePinDialog
          session={session}
          onClose={() => setChangePinOpen(false)}
          onChanged={(newPinValue) => {
            onProfileSaved({ pin: newPinValue });
            setChangePinOpen(false);
          }}
        />
      )}

      {historyOpen && (
        <HistoryDialog
          session={session}
          onClose={() => setHistoryOpen(false)}
        />
      )}
    </div>
  );
}

function InstaSnapshotOverlay({ rider, onClose }) {
  // True fullscreen story-mode. Covers the header (z-[100] > z-50) with pure black.
  // No chrome, no navigation — only the 9:16 card and a nearly invisible close button.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-[999] bg-black flex items-center justify-center"
      data-testid="passport-insta-mode"
    >
      {/* The 9:16 poster itself */}
      <div
        className="h-[100dvh] max-h-[100dvh] flex items-center justify-center"
        style={{ aspectRatio: "9/16" }}
      >
        <div className="w-full h-full max-h-full">
          <RaceCard
            rider={rider}
            variant="instagram"
            accent="#00E5FF"
            testId="insta-race-card"
          />
        </div>
      </div>

      {/* Ultra-discreet close: tiny X, 5% opacity — invisible on Instagram Story prints */}
      <button
        onClick={onClose}
        className="fixed top-2 right-2 z-[1000] w-7 h-7 flex items-center justify-center rounded-full bg-black/20 text-white opacity-5 hover:opacity-100 focus-visible:opacity-100 transition-opacity duration-200 print:hidden"
        aria-label="Fechar modo Snapshot"
        title="Fechar"
        data-testid="insta-exit-btn"
      >
        <X size={14} strokeWidth={2} />
      </button>
    </div>
  );
}

function EditProfileDialog({ session, onClose, onSaved }) {
  const [form, setForm] = useState({
    rider_name: session.rider_name || "",
    motorcycle_model: session.motorcycle_model || "",
    photo_url: session.photo_url || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rider_name.trim()) {
      toast.error("Nome é obrigatório");
      return;
    }
    setSaving(true);
    try {
      await api.put("/passport/profile", {
        phone: session.phone,
        pin: session.pin,
        rider_name: form.rider_name.trim(),
        motorcycle_model: form.motorcycle_model.trim(),
        photo_url: form.photo_url.trim(),
      });
      toast.success("Dados atualizados!");
      onSaved(form);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const handlePickPhoto = () => {
    if (uploading) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so same file can be re-selected
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Selecione uma imagem (JPG, PNG ou WEBP)");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Imagem muito grande (máx 8 MB)");
      return;
    }

    const fd = new FormData();
    fd.append("phone", session.phone);
    fd.append("pin", session.pin);
    fd.append("file", file);

    setUploading(true);
    try {
      const res = await fetch(`${API}/passport/photo`, {
        method: "POST",
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.detail || "Falha no upload");
      }
      const nextUrl = data.photo_url;
      setForm((prev) => ({ ...prev, photo_url: nextUrl }));
      // Reflect in the RaceCard behind the dialog immediately (backend already persisted it).
      onSaved({
        rider_name: form.rider_name,
        motorcycle_model: form.motorcycle_model,
        photo_url: nextUrl,
        _keepOpen: true,
      });
      toast.success("Foto atualizada!");
    } catch (err) {
      toast.error(err?.message || "Falha ao enviar foto");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="passport-edit-dialog"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md border border-white/10 bg-[#12141D] p-6 space-y-4"
      >
        <div className="flex items-center gap-2">
          <Settings size={18} style={{ color: "#00E5FF" }} />
          <h3 className="font-display text-xl text-white uppercase">
            Editar Passaporte
          </h3>
        </div>

        <div>
          <Label className="label-caps block mb-2">Nome *</Label>
          <Input
            value={form.rider_name}
            onChange={(e) => setForm({ ...form, rider_name: e.target.value })}
            className="bg-[#0B0D14] border-white/10 focus:border-[#00E5FF] rounded-sm h-11"
            data-testid="passport-edit-name"
          />
        </div>

        <div>
          <Label className="label-caps block mb-2">Modelo da Moto</Label>
          <Input
            value={form.motorcycle_model}
            onChange={(e) =>
              setForm({ ...form, motorcycle_model: e.target.value })
            }
            className="bg-[#0B0D14] border-white/10 focus:border-[#00E5FF] rounded-sm h-11"
            data-testid="passport-edit-moto"
          />
        </div>

        <div>
          <Label className="label-caps block mb-2">Foto do Piloto</Label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            onChange={handleFileChange}
            className="hidden"
            data-testid="passport-edit-photo-input"
          />

          <div className="flex items-center gap-3">
            <div className="w-20 h-20 border border-white/10 bg-[#0B0D14] overflow-hidden flex items-center justify-center shrink-0">
              {form.photo_url ? (
                <img
                  src={form.photo_url}
                  alt="Foto atual"
                  referrerPolicy="no-referrer"
                  loading="eager"
                  decoding="async"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.opacity = "0.2";
                  }}
                />
              ) : (
                <Camera size={22} className="text-white/40" />
              )}
            </div>

            <button
              type="button"
              onClick={handlePickPhoto}
              disabled={uploading}
              className="flex-1 border border-white/15 hover:border-[#00E5FF] px-4 py-3 text-xs uppercase tracking-widest text-white inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="passport-upload-photo-btn"
            >
              {uploading ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Enviando...
                </>
              ) : (
                <>
                  <Upload size={14} />
                  {form.photo_url ? "Trocar Foto" : "Enviar Foto"}
                </>
              )}
            </button>
          </div>
          <p className="text-[11px] text-white/50 mt-2">
            JPG, PNG ou WEBP até 8 MB. A foto é enviada para nossa nuvem e fica em alta resolução no seu Race Card.
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="border border-white/15 text-white px-4 py-2 text-xs uppercase tracking-widest hover:border-white transition-colors"
          >
            Cancelar
          </button>
          <Button
            type="submit"
            disabled={saving}
            className="btn-primary px-5 py-2 text-xs"
            data-testid="passport-edit-save"
          >
            {saving ? "Salvando..." : "Salvar"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function ChangePinDialog({ session, onClose, onChanged }) {
  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (currentPin.length !== 4) {
      toast.error("Informe seu PIN atual (4 dígitos)");
      return;
    }
    if (!/^\d{4}$/.test(newPin)) {
      toast.error("Novo PIN precisa ter 4 dígitos");
      return;
    }
    if (newPin !== confirmPin) {
      toast.error("A confirmação do novo PIN não confere");
      return;
    }
    if (newPin === currentPin) {
      toast.error("O novo PIN precisa ser diferente do atual");
      return;
    }
    setSaving(true);
    try {
      const res = await api.post("/passport/change-pin", {
        phone: session.phone,
        current_pin: currentPin,
        new_pin: newPin,
        new_pin_confirm: confirmPin,
      });
      toast.success("PIN atualizado com sucesso!");
      onChanged(res.data?.pin || newPin);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao trocar PIN");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="passport-change-pin-dialog"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md border border-white/10 bg-[#12141D] p-6 space-y-4"
      >
        <div className="flex items-center gap-2">
          <Lock size={18} style={{ color: "#F59E0B" }} />
          <h3 className="font-display text-xl text-white uppercase">
            Trocar PIN
          </h3>
        </div>
        <p className="text-white/70 text-xs leading-snug">
          Confirme seu PIN atual, escolha um novo e digite de novo para confirmar.
        </p>

        <div>
          <Label className="label-caps block mb-2">PIN atual</Label>
          <Input
            type="password"
            inputMode="numeric"
            autoFocus
            value={currentPin}
            onChange={(e) =>
              setCurrentPin(e.target.value.replace(/\D/g, "").slice(0, 4))
            }
            placeholder="****"
            maxLength={4}
            className="bg-[#0B0D14] border-white/10 focus:border-[#F59E0B] rounded-sm h-14 text-center font-display text-3xl tracking-[0.5em]"
            data-testid="passport-current-pin-input"
          />
        </div>

        <div>
          <Label className="label-caps block mb-2">Novo PIN</Label>
          <Input
            type="password"
            inputMode="numeric"
            value={newPin}
            onChange={(e) =>
              setNewPin(e.target.value.replace(/\D/g, "").slice(0, 4))
            }
            placeholder="****"
            maxLength={4}
            className="bg-[#0B0D14] border-white/10 focus:border-[#39FF14] rounded-sm h-14 text-center font-display text-3xl tracking-[0.5em]"
            data-testid="passport-change-new-pin-input"
          />
        </div>

        <div>
          <Label className="label-caps block mb-2">Confirmar novo PIN</Label>
          <Input
            type="password"
            inputMode="numeric"
            value={confirmPin}
            onChange={(e) =>
              setConfirmPin(e.target.value.replace(/\D/g, "").slice(0, 4))
            }
            placeholder="****"
            maxLength={4}
            className="bg-[#0B0D14] border-white/10 focus:border-[#39FF14] rounded-sm h-14 text-center font-display text-3xl tracking-[0.5em]"
            data-testid="passport-change-confirm-pin-input"
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="border border-white/15 text-white px-4 py-2 text-xs uppercase tracking-widest hover:border-white transition-colors"
          >
            Cancelar
          </button>
          <Button
            type="submit"
            disabled={saving}
            className="btn-primary px-5 py-2 text-xs"
            data-testid="passport-change-pin-save"
          >
            {saving ? "Salvando..." : "Salvar novo PIN"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function HistoryDialog({ session, onClose }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .post("/passport/history", { phone: session.phone, pin: session.pin })
      .then((res) => {
        if (cancelled) return;
        setItems(res.data?.items || []);
        setTotal(res.data?.total || 0);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.detail || "Não foi possível carregar seu histórico");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [session.phone, session.pin]);

  const officialCount = items.filter((r) => r.is_official).length;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="passport-history-dialog"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg border border-white/10 bg-[#12141D] p-6 max-h-[85vh] flex flex-col"
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Flag size={16} style={{ color: "#00E5FF" }} />
              <span className="label-caps" style={{ color: "#00E5FF" }}>
                Meus Rastros
              </span>
            </div>
            <h3 className="font-display text-2xl sm:text-3xl uppercase text-white leading-tight">
              🏁 Histórico de Rolês
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="border border-white/15 hover:border-white p-2 text-white transition-colors"
            aria-label="Fechar"
            data-testid="passport-history-close"
          >
            <X size={14} />
          </button>
        </div>

        <div
          className="border-2 border-dashed px-5 py-3 flex items-center justify-between gap-4 mb-4"
          style={{
            borderColor: "rgba(0,229,255,0.45)",
            background: "rgba(0,229,255,0.05)",
          }}
          data-testid="passport-history-total"
        >
          <div>
            <div className="label-caps text-white/60">Total de rolês</div>
            <div className="font-display text-3xl text-[#00E5FF] glow-blue leading-none mt-1">
              {total}
            </div>
          </div>
          <div className="text-right">
            <div className="label-caps text-white/60">Oficiais</div>
            <div className="font-display text-xl text-[#39FF14] leading-none mt-1">
              {officialCount}
            </div>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 -mx-1 px-1">
          {loading ? (
            <div className="text-white/60 text-sm text-center py-6">
              Carregando seus rastros...
            </div>
          ) : error ? (
            <div className="text-[#F59E0B] text-sm text-center py-6">{error}</div>
          ) : items.length === 0 ? (
            <div className="text-white/60 text-sm text-center py-6">
              Nenhum rolê registrado ainda. Bora acelerar! 🏍️
            </div>
          ) : (
            <ul className="space-y-2" data-testid="passport-history-list">
              {items.map((it) => {
                const past = isPast(it.ride_datetime);
                return (
                  <li
                    key={it.ride_id}
                    className="border border-white/10 bg-[#0B0D14] px-4 py-3"
                    data-testid={`passport-history-item-${it.ride_id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white font-body text-sm sm:text-base truncate">
                            {it.name}
                          </span>
                          {!it.is_official && (
                            <span
                              className="label-caps text-[10px] px-1.5 py-0.5 border shrink-0"
                              style={{
                                color: "#F59E0B",
                                borderColor: "rgba(245,158,11,0.35)",
                              }}
                            >
                              Livre
                            </span>
                          )}
                          {it.waitlist_position ? (
                            <span
                              className="label-caps text-[10px] px-1.5 py-0.5 border shrink-0"
                              style={{
                                color: "#F59E0B",
                                borderColor: "rgba(245,158,11,0.55)",
                              }}
                            >
                              Fila #{it.waitlist_position}
                            </span>
                          ) : null}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-white/60">
                          <span className="inline-flex items-center gap-1">
                            <Calendar size={12} /> {formatDatePt(it.ride_datetime)}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <MapPin size={12} /> {it.location}
                          </span>
                        </div>
                      </div>
                      <span
                        className="label-caps text-[10px] shrink-0 mt-1"
                        style={{ color: past ? "#39FF14" : "#00E5FF" }}
                      >
                        {past ? "Concluído" : "Próximo"}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
